import {
  Button,
  Col,
  Icon,
  Input,
  RadioButton,
  RadioGroup,
  Row,
  Select,
  SelectOption,
  Text,
  Tooltip,
  message,
} from 'components';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { OBJECT_ID_REGEX } from 'utils/constants';
import findOneRosterQa from '../../../../api/UserMlsRosterController/findOneRosterQa';
import RosterDataViewerPageContext from './RosterDataViewerPageContext';

const buildBoardElement = (
  board: string,
  boardId: string,
  fullName: string,
) => (
    <SelectOption key={boardId} value={boardId} label={board}>
      <Text>{board}</Text>&nbsp;<Text type="secondary">{fullName}</Text>
    </SelectOption>
  );

const RosterFinder: React.FC = () => {
  const {
    boards,
    rosterType,
    setRosterType,
    rosterMlsNumber,
    setRosterMlsNumber,
    rosterBoardId,
    setRosterBoardId,
    rosterMlsUniqueId,
    setRosterMlsUniqueId,
    setRoster,
    setUpdates,
  } = useContext(RosterDataViewerPageContext);

  const getRoster = async () => {
    const validNumber =
      (Boolean(rosterMlsNumber) || Boolean(rosterMlsUniqueId)) &&
      Boolean(rosterBoardId) === Boolean(rosterBoardId.match(OBJECT_ID_REGEX));
    if (!validNumber)
      return message.error('Not enough info entered to find a roster user');

    try {
      const res = await findOneRosterQa({
        mlsNumber: rosterMlsNumber,
        boardId: rosterBoardId,
        mlsUniqueId: rosterMlsUniqueId,
        rosterType,
      });
      setRoster(res);
      setRosterMlsNumber(res.memberNumber || res.uid.replace('rets-', ''));
      setRosterBoardId(res.boardId.toString());
      setRosterMlsUniqueId(res.mlsUniqueId);
      setUpdates(undefined);
    } catch (e) {
      message.error(
        e?.response?.data?.message || 'Could not find Roster Users',
      );
    }
  };

  return (
    <StyledForm>
      <Row gutter={16}>
        <Col>
          <RadioGroup
            value={rosterType}
            label="Roster Type"
            buttonStyle="solid"
            size="small"
            onChange={({ target: { value } }) => setRosterType(value)}
          >
            <RadioButton value="usermlsroster">Agent Roster</RadioButton>
            <RadioButton value="usermlsrosterinactive">
              Inactive Roster
            </RadioButton>
            <RadioButton value="usermlsrosternonagent">
              Non-Agent Roster
            </RadioButton>
          </RadioGroup>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Input
            label={
              <>
                Roster MLS Number{' '}
                <Tooltip title="In the roster this is the uid field without rets- prefix or memberNumber field if it is a TREB/PropTx roster entry">
                  <span>
                    <Icon
                      color="info"
                      width={14}
                      height={14}
                      type="antdInfoCircleOutlined"
                    />
                  </span>
                </Tooltip>
              </>
            }
            value={rosterMlsNumber}
            onChange={e => setRosterMlsNumber(e.target.value)}
          />
        </Col>
        <Col span={8}>
          <Select
            showSearch
            label={<>Roster Board (Always specify if TREB/PropTx)</>}
            allowClear
            filterOption={(input, option) =>
              (option!.label as string)
                .toUpperCase()
                .includes(input.toUpperCase())
            }
            value={rosterBoardId}
            onChange={boardId => setRosterBoardId(boardId || '')}
          >
            {boards!.map(board =>
              buildBoardElement(board.board, board._id, board.name),
            )}
          </Select>
        </Col>
        <Col span={8}>
          <Input
            label={
              <>
                Roster Unique MLS ID{' '}
                <Tooltip title="In the roster this is the mlsUniqueId field">
                  <span>
                    <Icon
                      color="info"
                      width={14}
                      height={14}
                      type="antdInfoCircleOutlined"
                    />
                  </span>
                </Tooltip>
              </>
            }
            value={rosterMlsUniqueId}
            onChange={e => setRosterMlsUniqueId(e.target.value)}
          />
        </Col>
      </Row>
      {rosterBoardId && (
        <Row gutter={16}>
          <Col span={12}>
            <Input
              label={<>Roster Board ID (for editing the board entry)</>}
              readOnly
              value={rosterBoardId}
            />
          </Col>
        </Row>
      )}
      <Button onClick={getRoster}>Find Roster</Button>
    </StyledForm>
  );
};

export default RosterFinder;

const StyledForm = styled.form`
  width: 1000px;
  display: grid;
  grid-gap: 15px;
`;
